import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/contexts/SessionContext";
import { useProfile } from "@/contexts/ProfileContext";
import { queryKeys } from "@/lib/query-keys";

interface AssetAccessResult {
  canDownload: boolean;
  isOwner: boolean;
  hasPurchased: boolean;
  isFree: boolean;
}

export function useAssetAccess(assetId: string | undefined, sellerId: string | undefined, isFree: boolean = false) {
  const { user } = useSession();
  const { profile } = useProfile();

  const query = useQuery({
    // Keyed on the profile, not the auth user: the profile arrives after the
    // session, and a result computed in between ("not signed in") was cached
    // under the same key for a minute, so an owner or buyer saw Buy buttons.
    queryKey: queryKeys.assets.access(assetId!, profile?.id),
    queryFn: async (): Promise<AssetAccessResult> => {
      if (!assetId) {
        return { canDownload: false, isOwner: false, hasPurchased: false, isFree };
      }

      // Not logged in
      if (!profile) {
        return { canDownload: isFree, isOwner: false, hasPurchased: false, isFree };
      }

      // Check if user is the owner
      const isOwner = sellerId === profile.id;

      if (isOwner) {
        return { canDownload: true, isOwner: true, hasPurchased: false, isFree };
      }

      const { data: purchase, error } = await supabase
        .from("purchases")
        .select("id")
        .eq("buyer_id", profile.id)
        .eq("asset_id", assetId)
        .eq("refunded", false)
        .maybeSingle();
      if (error) throw error;

      const hasPurchased = !!purchase;

      return {
        canDownload: hasPurchased || isFree,
        isOwner: false,
        hasPurchased,
        isFree,
      };
    },
    // A signed-in user waits for their profile rather than being answered as
    // a guest.
    enabled: !!assetId && (!user || !!profile),
    staleTime: 1000 * 60, // Cache for 1 minute
  });

  // A disabled query reports isLoading=false with no data, which the purchase
  // card read as "not owned" and rendered Buy buttons to the owner or a buyer
  // until their profile arrived. Waiting for the profile is loading.
  const waitingForProfile = !!assetId && !!user && !profile;
  return { ...query, isLoading: query.isLoading || waitingForProfile };
}
